import { createInertiaApp, router } from '@inertiajs/vue3';
import { configureEcho } from '@laravel/echo-vue';
import * as Sentry from '@sentry/vue';
import {
    browserTracingIntegration,
    feedbackIntegration,
    replayIntegration,
} from '@sentry/vue';
import { createApp, h } from 'vue';
import { initializeTheme } from '@/composables/useAppearance';
import AppLayout from '@/layouts/AppLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import EditorLayout from '@/layouts/EditorLayout.vue';
import SettingsLayout from '@/layouts/settings/Layout.vue';

if (typeof window !== 'undefined') {
    configureEcho({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: Number(import.meta.env.VITE_REVERB_PORT ?? 8080),
        forceTLS: false,
        enabledTransports: ['ws'],
        namespace: false,
    });
}

const appName = import.meta.env.VITE_APP_NAME || 'Rewio';
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) => {
        const pages = import.meta.glob<any>('./pages/**/*.vue', {
            eager: true,
        });

        return pages[`./pages/${name}.vue`];
    },
    layout: (name) => {
        switch (true) {
            case name === 'Welcome':
            case name === 'Pricing':
            case name === 'Workflow':
            case name === 'Error':
            case name === 'Editor/Print':
            case name.startsWith('Tutorial/'):
                return null;
            case name.startsWith('auth/'):
                return AuthLayout;
            case name.startsWith('Editor/'):
                return EditorLayout;
            case name.startsWith('settings/'):
                return [AppLayout, SettingsLayout];
            default:
                return AppLayout;
        }
    },
    progress: {
        color: '#4B5563',
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        if (sentryDsn) {
            Sentry.init({
                app,
                dsn: sentryDsn,
                environment: import.meta.env.VITE_SENTRY_ENVIRONMENT as
                    | string
                    | undefined,
                integrations: [
                    browserTracingIntegration(),
                    replayIntegration({
                        maskAllText: true,
                        blockAllMedia: true,
                    }),
                    feedbackIntegration({
                        autoInject: false,
                        colorScheme: 'system',
                        showBranding: false,
                        formTitle: 'Signaler un problème',
                        messagePlaceholder: 'Décrivez ce que vous observez…',
                        submitButtonLabel: 'Envoyer',
                        cancelButtonLabel: 'Annuler',
                        nameLabel: 'Nom',
                        emailLabel: 'E-mail',
                        successMessageText: 'Merci pour votre retour !',
                    }),
                ],
                tracesSampleRate: Number(
                    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
                ),
                // Replay only on errors to limit quota usage.
                replaysSessionSampleRate: 0.0,
                replaysOnErrorSampleRate: 1.0,
                sendDefaultPii: false,
            });

            // Tag every navigation with the server-side request id for cross-stack correlation.
            router.on('navigate', (event) => {
                const requestId = (
                    event.detail.page.props as Record<string, unknown>
                ).requestId;

                if (typeof requestId === 'string') {
                    Sentry.setTag('request_id', requestId);
                }
            });
        }

        app.use(plugin);

        if (el) {
            app.mount(el);
        }

        return app;
    },
});

// This will set light / dark mode on page load...
initializeTheme();
